/**
 * Raw Compass Data Service for FlowRide
 * Provides device orientation using sensor fusion (compass + gyroscope) or AbsoluteOrientationSensor
 * Emits raw, uncalibrated compass data
 */

import { SensorFusion, type SensorData } from "../../libs/SensorFusion";
import {
    CompassHeadingJumpFilter,
    extractAbsoluteCompassHeadingDetailed,
    normalizeCompassAngle,
    shortestHeadingDeltaDegrees,
    supportsAbsoluteDeviceOrientationEvent,
    type CompassOrientationEvent,
} from "../../tools/compass-heading.tools";
import { CompassData, NavigationEventType, type NavigationEvent } from "../../types/Navigation";
import { subscribeToPageVisibilityTransitions } from "../lifecycle/PageLifecycleStore";
import { compassPermissionService } from "./CompassPermissionService";

/**
 * Event listener callback type
 */
type CompassEventListener = ( data: CompassData ) => void;

/**
 * Sensor mode
 */
export enum SensorMode {

    /** Use sensor fusion (magnetometer + gyroscope + accelerometer) */
    "SENSOR_FUSION" = "sensor_fusion",

    /** Use AbsoluteOrientationSensor API */
    "ABSOLUTE_ORIENTATION" = "absolute_orientation",

    /** Use basic DeviceOrientationEvent */
    "BASIC" = "basic",
}

/**
 * Service that provides raw compass data from device sensors
 */
export class RawCompassDataService {

    /** Singleton instance */
    private static instance: RawCompassDataService | null = null;

    /** Last compass data */
    private lastValue: CompassData = {
        "heading":    0,
        "calibrated": false,
        "offset":     0,
        "timestamp":  0,
    };

    /** Event listeners */
    private listeners: Set<CompassEventListener> = new Set();

    /** Current sensor mode */
    private sensorMode: SensorMode = SensorMode.BASIC;

    /** Sensor fusion instance */
    private sensorFusion: SensorFusion | null = null;

    /** AbsoluteOrientationSensor instance */
    private absoluteOrientationSensor: AbsoluteOrientationSensor | null = null;

    /** Whether the service is active */
    private active: boolean = false;

    /** In-flight {@link start} promise to prevent concurrent double-start races. */
    private startInFlight: Promise<void> | null = null;

    /** Calibration offset (applied externally by calibration service) */
    private calibrationOffset: number = 0;

    /** Whether compass is calibrated */
    private calibrated: boolean = false;

    /** Last raw heading (before calibration) */
    private lastRawHeading: number | null = null;

    /** Timestamp (ms) of the last accepted absolute heading sample. */
    private lastHeadingSampleMs: number = 0;

    /**
     * Estimated yaw rate (°/s) from successive absolute samples (and optional gyro assist).
     * Used to extrapolate the cone between sparse Firefox orientation events.
     */
    private headingRateDegPerSec: number = 0;

    /** Optional DeviceMotion yaw rate (°/s) when available; blended with sample-derived rate. */
    private gyroYawRateDegPerSec: number = 0;

    /** Whether a gyro assist sample has been seen recently. */
    private gyroAssistSeen: boolean = false;

    /**
     * Last relative `deviceorientation` alpha — motion assist only when absolute yaw freezes.
     */
    private lastRelativeAlphaDeg: number | null = null;

    /** Timestamp of {@link lastRelativeAlphaDeg}. */
    private lastRelativeAlphaMs: number = 0;

    /** Last time an absolute sample changed heading by more than ~0.5°. */
    private lastAbsoluteMotionMs: number = 0;

    /** Last north-referenced heading reported by the absolute sensor (before assist). */
    private lastAbsoluteReportedHeading: number | null = null;

    /** Timestamp of last gyro rate sample (for extrapolation timing only — not integrated into heading). */
    private lastGyroSampleMs: number = 0;

    /** Soft flat↔tilt hysteresis state for absolute Euler formula selection. */
    private usingFlatFormula: boolean = true;

    /** Absolute samples that change by less than this are treated as frozen noise. */
    private readonly ABSOLUTE_MOTION_EPSILON_DEG = 0.4;

    /** Max extrapolation ahead of the last absolute sample. */
    private readonly MAX_EXTRAPOLATION_MS = 180;

    /** Cap yaw rate used for prediction (avoids 500°/s overshoot from sparse accepts). */
    private readonly PREDICT_RATE_CAP_DEG_PER_SEC = 120;

    /** Display follow time constant (s) for small errors — lower = more reactive. */
    private readonly DISPLAY_TAU_SEC = 0.038;

    /** How hard an accepted absolute sample pulls the display heading (0–1). */
    private readonly DISPLAY_ACCEPT_BLEND = 0.62;

    /** Prefer gyro extrapolation only after absolute samples go this stale (ms). */
    private readonly GYRO_PREDICT_AFTER_MS = 70;

    /** Absolute idle above this → allow relative motion assist (Chromium freeze). */
    private readonly ABSOLUTE_FREEZE_MS = 250;

    /** Continuously followed heading for the UI (prediction target + soft catch-up). */
    private displayHeadingDeg: number | null = null;

    /** Wall-clock of the last display follow step. */
    private lastDisplayFollowMs: number = 0;

    /** Unsubscribe for PageLifecycle visibility resume → prediction reset. */
    private pageVisibilityUnsubscribe: ( () => void ) | null = null;

    /** Sanity clamp for instantaneous heading rate (°/s). */
    private readonly MAX_HEADING_RATE_DEG_PER_SEC = 720;

    /** EMA coefficient when updating sample-derived yaw rate (higher = trust new sample more). */
    private readonly HEADING_RATE_EMA_ALPHA = 0.45;

    /** Update throttle interval in ms — kept near the sensor rate to stay responsive. */
    private readonly UPDATE_INTERVAL = 16; // ~60 fps

    /** Last update timestamp */
    private lastUpdateTime: number = 0;

    /**
     * Rejects transient ~90° Euler discontinuities common on Firefox Android.
     */
    private readonly headingJumpFilter: CompassHeadingJumpFilter = new CompassHeadingJumpFilter();

    /**
     * True once a north-referenced orientation sample has been observed.
     * Relative fallback is only used when the absolute event API is unavailable.
     */
    private absoluteOrientationSeen: boolean = false;

    /**
     * Whether one-shot user-gesture wake listeners are currently registered.
     * Firefox Android may not fire orientation until the first touch (Bug 1805110).
     */
    private userGestureWakeListenersBound: boolean = false;

    /**
     * Bound orientation handler kept as a stable reference for add/removeEventListener.
     */
    private readonly onDeviceOrientation = ( event: DeviceOrientationEvent ): void => {
        this.handleDeviceOrientation( event );
    };

    /**
     * One-shot user-activation handler that re-binds sensors after the first touch.
     */
    private readonly onUserGestureWake = (): void => {
        this.removeUserGestureWakeListeners();
        this.wakeSensorsFromUserGesture();
    };

    /**
     * DeviceMotion assist: track yaw rate only.
     * Used to extrapolate between absolute samples when the magnetometer freezes (Chromium).
     * Does NOT integrate into lastRawHeading — that fought Firefox absolute and caused lag.
     */
    private readonly onDeviceMotionAssist = ( event: DeviceMotionEvent ): void => {
        const rotationRate = event.rotationRate;
        if ( rotationRate === null ) {
            return;
        }

        /*
         * Prefer Z-axis (alpha): when the phone is flat, that is compass yaw.
         * Taking max(|α|,|β|,|γ|) mixed pitch/roll into heading prediction and caused jitter.
         */
        let yawRate: number | null = null;
        if ( typeof rotationRate.alpha === "number" && !Number.isNaN( rotationRate.alpha ) ) {
            yawRate = rotationRate.alpha;
        } else {
            for ( const value of [ rotationRate.beta, rotationRate.gamma ] ) {
                if ( typeof value === "number" && !Number.isNaN( value ) ) {
                    if ( yawRate === null || Math.abs( value ) > Math.abs( yawRate ) ) {
                        yawRate = value;
                    }
                }
            }
        }
        if ( yawRate === null ) {
            return;
        }

        const clamped = Math.max(
            -this.MAX_HEADING_RATE_DEG_PER_SEC,
            Math.min( this.MAX_HEADING_RATE_DEG_PER_SEC, yawRate ),
        );

        // Light EMA so DeviceMotion noise does not yank the cone.
        this.gyroYawRateDegPerSec = this.gyroAssistSeen
            ? 0.55 * this.gyroYawRateDegPerSec + 0.45 * clamped
            : clamped;
        this.gyroAssistSeen = true;
        this.lastGyroSampleMs = Date.now();
    };

    /**
     * Private constructor for singleton pattern
     */
    private constructor() {
    // Detect best available sensor mode
        this.detectBestSensorMode();
    }

    /**
     * Get singleton instance
     * @returns Service instance
     */
    public static getInstance(): RawCompassDataService {
        if ( RawCompassDataService.instance === null ) {
            RawCompassDataService.instance = new RawCompassDataService();
        }

        return RawCompassDataService.instance;
    }

    /**
     * Drop the singleton (Vite HMR only). Stops sensors so listeners are not duplicated.
     */
    public static resetInstanceForHotReload(): void {
        if ( RawCompassDataService.instance !== null ) {
            try {
                RawCompassDataService.instance.stop();
            } catch {
                // Ignore stop failures during hot dispose.
            }
            RawCompassDataService.instance = null;
        }
    }

    /**
     * Whether compass tracking is currently active.
     * @returns True when {@link start} has succeeded and {@link stop} has not been called.
     */
    public isActive(): boolean {
        return this.active;
    }

    /**
     * Get the last compass data
     * @returns Last compass data or null if no data available
     */
    public getLastValue(): CompassData {
        return this.lastValue;
    }

    /**
     * Whether at least one real sensor heading has been received.
     * The default {@link lastValue} heading is 0° (north) and must not be shown as a reading.
     * @returns True when a sensor-backed heading is available.
     */
    public hasValidReading(): boolean {
        return this.lastValue.timestamp > 0 && this.lastRawHeading !== null;
    }

    /**
     * Predicted calibrated heading for UI refresh between absolute samples.
     * Combines short rate/gyro extrapolation with a snappy display follow so the cone
     * feels fluid at 30 fps without reintroducing formula jumps or heavy lag.
     * @param nowMs - Prediction time in epoch milliseconds (defaults to Date.now()).
     * @returns Calibrated heading in [0, 360), or null when no sensor reading exists yet.
     */
    public getPredictedHeading( nowMs: number = Date.now() ): number | null {
        if ( this.lastRawHeading === null || this.lastHeadingSampleMs <= 0 ) {
            return null;
        }

        const targetRaw = this.computePredictedTargetRaw( nowMs );

        if ( this.displayHeadingDeg === null ) {
            this.displayHeadingDeg = targetRaw;
            this.lastDisplayFollowMs = nowMs;

            return normalizeCompassAngle( this.displayHeadingDeg + this.calibrationOffset );
        }

        const rawDtSec = ( nowMs - this.lastDisplayFollowMs ) / 1000;
        const dtSec = Math.max( 0, Math.min( rawDtSec, 0.05 ) );
        this.lastDisplayFollowMs = nowMs;

        const errorDeg = shortestHeadingDeltaDegrees( this.displayHeadingDeg, targetRaw );
        const absError = Math.abs( errorDeg );

        // Larger miss → faster catch-up so turns stay reactive.
        let tauSec = this.DISPLAY_TAU_SEC;
        if ( absError > 18 ) {
            tauSec = 0.022;
        } else if ( absError > 8 ) {
            tauSec = 0.028;
        } else if ( absError < 1.0 ) {
            tauSec = 0.05;
        }

        const alpha = dtSec <= 0 ? 1 : 1 - Math.exp( -dtSec / tauSec );
        this.displayHeadingDeg = normalizeCompassAngle( this.displayHeadingDeg + errorDeg * alpha );

        return normalizeCompassAngle( this.displayHeadingDeg + this.calibrationOffset );
    }

    /**
     * Clear prediction / display-follow state after a background → foreground transition.
     * Frozen tabs leave stale yaw rates and a large sample age; re-anchor on the last absolute
     * heading so the cone does not overshoot on resume.
     */
    public resetPredictionState(): void {
        this.headingRateDegPerSec = 0;
        this.gyroYawRateDegPerSec = 0;
        this.lastGyroSampleMs = 0;
        this.lastDisplayFollowMs = 0;
        this.displayHeadingDeg = this.lastRawHeading;
        if ( this.lastRawHeading !== null ) {
            this.lastHeadingSampleMs = Date.now();
        }
    }

    /**
     * Extrapolated raw heading target without mutating display-follow state.
     * @param nowMs - Wall-clock time in ms.
     * @returns Raw heading in [0, 360).
     */
    private computePredictedTargetRaw( nowMs: number ): number {
        if ( this.lastRawHeading === null || this.lastHeadingSampleMs <= 0 ) {
            return 0;
        }

        const ageMs = Math.max( 0, nowMs - this.lastHeadingSampleMs );
        const gyroAgeMs = this.lastGyroSampleMs > 0 ? nowMs - this.lastGyroSampleMs : Number.POSITIVE_INFINITY;
        const sampleRate = Math.max(
            -this.PREDICT_RATE_CAP_DEG_PER_SEC,
            Math.min( this.PREDICT_RATE_CAP_DEG_PER_SEC, this.headingRateDegPerSec ),
        );
        const gyroRate = Math.max(
            -this.PREDICT_RATE_CAP_DEG_PER_SEC,
            Math.min( this.PREDICT_RATE_CAP_DEG_PER_SEC, this.gyroYawRateDegPerSec ),
        );
        const gyroFresh = this.gyroAssistSeen && gyroAgeMs < 120;
        const gyroAbs = Math.abs( gyroRate );

        // Absolute samples own short gaps; gyro only fills freeze / sparse gaps.
        let rateDegPerSec = sampleRate;
        if ( gyroFresh && gyroAbs > 4 && ageMs >= this.GYRO_PREDICT_AFTER_MS ) {
            const gyroWeight = Math.min( 0.85, 0.35 + ( ageMs - this.GYRO_PREDICT_AFTER_MS ) / 250 );
            rateDegPerSec = gyroWeight * gyroRate + ( 1 - gyroWeight ) * sampleRate;
        }

        let targetRaw = this.lastRawHeading;
        if ( Math.abs( rateDegPerSec ) >= 1.5 ) {
            const extrapolateMs = Math.min( ageMs, this.MAX_EXTRAPOLATION_MS );
            const decay = extrapolateMs > 80
                ? Math.exp( -( extrapolateMs - 80 ) / 140 )
                : 1;
            targetRaw = this.lastRawHeading + rateDegPerSec * ( extrapolateMs / 1000 ) * decay;
        }

        return normalizeCompassAngle( targetRaw );
    }

    /**
     * Detect best available sensor mode.
     * Prefer AbsoluteOrientationSensor, then absolute DeviceOrientation events.
     * Avoid defaulting to SENSOR_FUSION: Firefox feeds relative Game Rotation Vector
     * into `deviceorientation`, which is not north-referenced and jumps near Euler singularities.
     */
    private detectBestSensorMode(): void {
        /*
         * Prefer DeviceOrientation absolute events over AbsoluteOrientationSensor.
         * On Brave/Chromium, AbsoluteOrientationSensor is often blocked by the site
         * "Motion sensors" setting and emits nothing, while deviceorientationabsolute still works.
         */
        if ( typeof DeviceOrientationEvent !== "undefined" ) {
            this.sensorMode = SensorMode.BASIC;

            return;
        }

        if ( typeof window !== "undefined" && "AbsoluteOrientationSensor" in window ) {
            this.sensorMode = SensorMode.ABSOLUTE_ORIENTATION;

            return;
        }

        this.sensorMode = SensorMode.BASIC;
    }


    /**
     * Set sensor mode manually
     * @param mode - Sensor mode
     */
    public setSensorMode( mode: SensorMode ): void {
        if ( this.active ) {
            console.warn( "[RawCompassDataService] Cannot change mode while active" );

            return;
        }

        this.sensorMode = mode;
    }

    /**
     * Get current sensor mode
     * @returns Current sensor mode
     */
    public getSensorMode(): SensorMode {
        return this.sensorMode;
    }

    /**
     * Set calibration offset
     * @param offset - Offset in degrees
     */
    public setCalibrationOffset( offset: number ): void {
        this.calibrationOffset = offset;
        this.calibrated = true;

        // Immediately update with new calibration
        if ( this.lastRawHeading !== null ) {
            this.emitCompassData( this.lastRawHeading );
        }
    }

    /**
     * Clear calibration
     */
    public clearCalibration(): void {
        this.calibrationOffset = 0;
        this.calibrated = false;

        // Immediately update without calibration
        if ( this.lastRawHeading !== null ) {
            this.emitCompassData( this.lastRawHeading );
        }
    }

    /**
     * Start compass tracking
     */
    public async start(): Promise<void> {
        if ( this.active ) {
            return;
        }

        if ( this.startInFlight !== null ) {
            await this.startInFlight;

            return;
        }

        this.startInFlight = this.runStart();
        try {
            await this.startInFlight;
        } finally {
            this.startInFlight = null;
        }
    }

    /**
     * Internal start implementation (single-flight via {@link start}).
     */
    private async runStart(): Promise<void> {
        if ( this.active ) {
            return;
        }

        /*
         * Request permission if needed using CompassPermissionService.
         * iOS gates the request behind a user gesture: passive start paths
         * never prompt; explicit entry points (current-location FAB, navigate
         * button, calibration UI) call requestPermission() inside the gesture.
         */
        const hasPermission = compassPermissionService.requiresUserGestureForPermission()
            ? compassPermissionService.isPermissionGranted()
            : await compassPermissionService.requestPermission();
        if ( !hasPermission ) {
            console.warn( "[RawCompassDataService] Permission not granted yet" );

            // Still listen for a user gesture so Chromium/iOS can grant later.
            this.installUserGestureWakeListeners();

            return;
        }

        this.active = true;
        this.absoluteOrientationSeen = false;
        this.headingJumpFilter.reset();
        this.startSelectedSensorMode();
        this.installUserGestureWakeListeners();
        this.installPageVisibilityPredictionReset();
    }

    /**
     * Start sensors for the currently selected {@link sensorMode}.
     */
    private startSelectedSensorMode(): void {
        switch ( this.sensorMode ) {
            case SensorMode.ABSOLUTE_ORIENTATION:
                this.startAbsoluteOrientationSensor();
                break;

            case SensorMode.SENSOR_FUSION:
                this.startSensorFusion();
                break;

            case SensorMode.BASIC:
                this.startBasicOrientation();
                break;
        }
    }


    /**
     * Pause Sensor events so that it is not overwritten during a streaming of injected data event
     */
    public pause() {
        this.detachOrientationAndMotionListeners();

        if ( this.absoluteOrientationSensor !== null ) {
            this.absoluteOrientationSensor.stop();
            this.absoluteOrientationSensor = null;
        }

        this.sensorFusion = null;
    }

    /**
     * Restart sensors events
     */
    public resume() {
        this.absoluteOrientationSeen = false;
        this.headingJumpFilter.reset();
        this.startSelectedSensorMode();
        this.installUserGestureWakeListeners();
    }

    /**
     * Re-bind orientation sensors after a user gesture.
     * Work around Firefox Android occasionally delaying DeviceOrientation until first touch.
     * Safe to call multiple times while the service is active.
     */
    public wakeSensorsFromUserGesture(): void {
        if ( !this.active ) {
            // Permission may require a user gesture on Chromium / Brave — retry full start.
            void this.start();

            return;
        }

        // Re-request permission inside the gesture when the API exists (Chromium Ask mode).
        void compassPermissionService.requestPermission().then( ( granted ) => {
            if ( !granted ) {
                return;
            }
            this.lastUpdateTime = 0;
            this.startSelectedSensorMode();
        } );

        // Allow the next orientation sample through the throttle immediately.
        this.lastUpdateTime = 0;

        switch ( this.sensorMode ) {
            case SensorMode.ABSOLUTE_ORIENTATION:
                if ( this.absoluteOrientationSensor !== null ) {
                    try {
                        this.absoluteOrientationSensor.stop();
                    } catch {
                        // Sensor may already be stopped.
                    }
                    this.absoluteOrientationSensor = null;
                }
                this.startAbsoluteOrientationSensor();
                break;

            case SensorMode.SENSOR_FUSION:
                this.detachOrientationAndMotionListeners();
                this.attachOrientationListeners();
                window.addEventListener( "devicemotion", this.handleDeviceMotion );
                break;

            case SensorMode.BASIC:
                this.attachOrientationListeners();
                this.attachGyroAssistListener();
                break;
        }
    }

    /**
     * Register one-shot listeners that wake sensors on the first user activation.
     */
    private installUserGestureWakeListeners(): void {
        if ( this.userGestureWakeListenersBound || typeof window === "undefined" ) {
            return;
        }

        this.userGestureWakeListenersBound = true;
        const options: AddEventListenerOptions = {
            "capture": true,
            "passive": true,
        };
        window.addEventListener( "pointerdown", this.onUserGestureWake, options );
        window.addEventListener( "touchstart", this.onUserGestureWake, options );
    }

    /**
     * Remove one-shot user-gesture wake listeners.
     */
    private removeUserGestureWakeListeners(): void {
        if ( !this.userGestureWakeListenersBound || typeof window === "undefined" ) {
            return;
        }

        this.userGestureWakeListenersBound = false;
        window.removeEventListener( "pointerdown", this.onUserGestureWake, true );
        window.removeEventListener( "touchstart", this.onUserGestureWake, true );
    }

    /**
     * Stop compass tracking
     */
    public stop(): void {
        if ( !this.active ) {
            return;
        }

        this.active = false;
        this.removeUserGestureWakeListeners();
        this.removePageVisibilityPredictionReset();
        this.pause();
        this.absoluteOrientationSeen = false;
        this.headingJumpFilter.reset();
        this.headingRateDegPerSec = 0;
        this.gyroYawRateDegPerSec = 0;
        this.gyroAssistSeen = false;
        this.lastRelativeAlphaDeg = null;
        this.lastRelativeAlphaMs = 0;
        this.lastAbsoluteMotionMs = 0;
        this.lastAbsoluteReportedHeading = null;
        this.lastGyroSampleMs = 0;
        this.usingFlatFormula = true;
        this.displayHeadingDeg = null;
        this.lastDisplayFollowMs = 0;
    }

    /**
     * On app/tab resume (PageLifecycleStore — same source as usePageVisibility),
     * clear stale prediction so a frozen background gap does not overshoot the cone.
     */
    private installPageVisibilityPredictionReset(): void {
        if ( this.pageVisibilityUnsubscribe !== null || typeof window === "undefined" ) {
            return;
        }

        this.pageVisibilityUnsubscribe = subscribeToPageVisibilityTransitions( ( isVisible ) => {
            if ( !isVisible || !this.active ) {
                return;
            }

            this.resetPredictionState();
        } );
    }

    /**
     * Detach the page-visibility prediction reset listener.
     */
    private removePageVisibilityPredictionReset(): void {
        if ( this.pageVisibilityUnsubscribe === null ) {
            return;
        }

        this.pageVisibilityUnsubscribe();
        this.pageVisibilityUnsubscribe = null;
    }

    /**
     * Remove window orientation / motion listeners registered by this service.
     */
    private detachOrientationAndMotionListeners(): void {
        window.removeEventListener( "deviceorientationabsolute", this.onDeviceOrientation as EventListener );
        window.removeEventListener( "deviceorientation", this.onDeviceOrientation );
        window.removeEventListener( "devicemotion", this.handleDeviceMotion );
        window.removeEventListener( "devicemotion", this.onDeviceMotionAssist );
    }

    /**
     * Attach absolute-first orientation listeners.
     * Firefox Android needs `deviceorientationabsolute` (Rotation Vector); default
     * `deviceorientation` uses Game Rotation Vector and is not a compass.
     */
    private attachOrientationListeners(): void {
        this.detachOrientationAndMotionListeners();

        if ( supportsAbsoluteDeviceOrientationEvent() ) {
            window.addEventListener( "deviceorientationabsolute", this.onDeviceOrientation as EventListener );
        }

        // Keep relative listener as last-resort fallback when absolute never fires.
        window.addEventListener( "deviceorientation", this.onDeviceOrientation );
    }

    /**
     * Attach gyro rate assist used to extrapolate heading between absolute samples.
     */
    private attachGyroAssistListener(): void {
        window.removeEventListener( "devicemotion", this.onDeviceMotionAssist );
        window.addEventListener( "devicemotion", this.onDeviceMotionAssist );
    }

    /**
     * Start AbsoluteOrientationSensor
     */
    private startAbsoluteOrientationSensor(): void {
        try {
            if ( !window.AbsoluteOrientationSensor ) {
                console.error( "[RawCompassDataService] AbsoluteOrientationSensor not available" );
                this.sensorMode = SensorMode.BASIC;
                this.startBasicOrientation();

                return;
            }

            const sensor = new window.AbsoluteOrientationSensor( { "frequency": 30 } );
            let hasLoggedSensorError = false;

            sensor.addEventListener( "reading", () => {
                if ( sensor.quaternion === null ) {
                    return;
                }

                const heading = this.quaternionToHeading( sensor.quaternion );
                this.acceptFilteredHeading( heading );
            } );

            sensor.addEventListener( "error", ( event: Event ) => {
                if ( hasLoggedSensorError ) {
                    return;
                }
                hasLoggedSensorError = true;
                const errorEvent = event as Event & { "error"?: Error };
                console.warn( "[RawCompassDataService] AbsoluteOrientationSensor error (suppressed after first):", errorEvent.error );
            } );

            sensor.start();
            this.absoluteOrientationSensor = sensor;

            /*
             * Parallel DeviceOrientation listeners: AbsoluteOrientationSensor is often blocked
             * on Brave (motion sensors site setting) and may never emit readings.
             */
            this.attachOrientationListeners();
            this.attachGyroAssistListener();

            // If no absolute reading arrives soon, keep BASIC listeners (already attached).
            window.setTimeout( () => {
                if ( this.active && !this.hasValidReading() && this.sensorMode === SensorMode.ABSOLUTE_ORIENTATION ) {
                    console.warn( "[RawCompassDataService] AbsoluteOrientationSensor produced no readings; relying on deviceorientation fallback" );
                    this.sensorMode = SensorMode.BASIC;
                }
            }, 1500 );
        } catch( error ) {
            console.error( "[RawCompassDataService] Failed to start AbsoluteOrientationSensor:", error );

            // Fallback to basic orientation
            this.sensorMode = SensorMode.BASIC;
            this.startBasicOrientation();
        }
    }

    /**
     * Start sensor fusion mode
     */
    private startSensorFusion(): void {
        this.sensorFusion = new SensorFusion( 0, 0.98 );

        // Absolute orientation for magnetometer-like heading + motion for gyro assist.
        this.attachOrientationListeners();
        window.addEventListener( "devicemotion", this.handleDeviceMotion );
    }

    /**
     * Start basic orientation mode
     */
    private startBasicOrientation(): void {
        this.attachOrientationListeners();
        this.attachGyroAssistListener();
    }

    /**
     * Handle device orientation event
     * @param event - Device orientation event
     */
    private handleDeviceOrientation = ( event: DeviceOrientationEvent ): void => {
        const now = Date.now();
        const orientationEvent = event as CompassOrientationEvent;
        const eventType = ( event as Event ).type;

        const alpha =
            typeof orientationEvent.alpha === "number" && !Number.isNaN( orientationEvent.alpha )
                ? orientationEvent.alpha
                : null;

        /*
         * Prefer the absolute event type for north. Relative Game RV is fallback only.
         * Once absolute has been seen, drop relative events entirely so they cannot
         * starve the throttle or corrupt the heading.
         */
        const isAbsoluteEventType =
            eventType === "deviceorientationabsolute" ||
            typeof orientationEvent.webkitCompassHeading === "number";

        if ( !isAbsoluteEventType && this.absoluteOrientationSeen ) {
            /*
             * Absolute north is already established. Use relative Game RV only as a
             * motion assist when absolute yaw is frozen (common when the phone is flat).
             */
            if ( alpha !== null ) {
                this.applyRelativeMotionAssist( alpha, now );
            }

            return;
        }

        const isAbsoluteSample =
            isAbsoluteEventType || orientationEvent.absolute === true;

        if ( isAbsoluteSample ) {
            this.absoluteOrientationSeen = true;
        }

        const extraction = extractAbsoluteCompassHeadingDetailed( orientationEvent, {
            "allowRelativeFallback":
                !supportsAbsoluteDeviceOrientationEvent() && !this.absoluteOrientationSeen,
            "usingFlatFormula": this.usingFlatFormula,
        } );
        this.usingFlatFormula = extraction.usingFlatFormula;
        const heading = extraction.heading;

        if ( heading === null ) {
            return;
        }

        /*
         * When absolute yaw is stuck (same north reading), do not overwrite the working
         * heading — relative/gyro assist may be advancing it. Re-anchor only on a real change.
         */
        if (
            isAbsoluteSample &&
            this.lastAbsoluteReportedHeading !== null &&
            Math.abs( shortestHeadingDeltaDegrees( this.lastAbsoluteReportedHeading, heading ) ) < this.ABSOLUTE_MOTION_EPSILON_DEG
        ) {
            // Soft decay only — keep enough sample rate for short-gap prediction.
            this.headingRateDegPerSec *= 0.97;
            if ( Math.abs( this.headingRateDegPerSec ) < 0.35 ) {
                this.headingRateDegPerSec = 0;
            }

            return;
        }

        // Throttle only after a usable heading exists — relative nulls must not steal slots.
        if ( now - this.lastUpdateTime < this.UPDATE_INTERVAL ) {
            return;
        }

        this.lastUpdateTime = now;
        if ( isAbsoluteSample ) {
            this.lastAbsoluteReportedHeading = heading;
        }
        this.acceptFilteredHeading( heading );
    };

    /**
     * Advance working heading from relative alpha deltas when absolute magnetometer yaw is stuck.
     * Next absolute sample re-anchors north via {@link acceptFilteredHeading}.
     * @param relativeAlphaDeg - Relative Game RV alpha degrees.
     * @param nowMs - Event time in ms.
     */
    private applyRelativeMotionAssist( relativeAlphaDeg: number, nowMs: number ): void {
        if ( this.lastRawHeading === null ) {
            this.lastRelativeAlphaDeg = relativeAlphaDeg;
            this.lastRelativeAlphaMs = nowMs;

            return;
        }

        if ( this.lastRelativeAlphaDeg === null || this.lastRelativeAlphaMs <= 0 ) {
            this.lastRelativeAlphaDeg = relativeAlphaDeg;
            this.lastRelativeAlphaMs = nowMs;

            return;
        }

        // Absolute still moving → do not fight it with Game RV deltas.
        const absoluteIdleMs = nowMs - this.lastAbsoluteMotionMs;
        if ( absoluteIdleMs < this.ABSOLUTE_FREEZE_MS ) {
            this.lastRelativeAlphaDeg = relativeAlphaDeg;
            this.lastRelativeAlphaMs = nowMs;

            return;
        }

        // Stale sample rate must not block assist once absolute is clearly frozen.
        if ( Math.abs( this.headingRateDegPerSec ) > 8 && absoluteIdleMs < this.ABSOLUTE_FREEZE_MS + 150 ) {
            this.lastRelativeAlphaDeg = relativeAlphaDeg;
            this.lastRelativeAlphaMs = nowMs;

            return;
        }

        if ( absoluteIdleMs >= this.ABSOLUTE_FREEZE_MS + 150 ) {
            this.headingRateDegPerSec = 0;
        }

        const deltaSec = ( nowMs - this.lastRelativeAlphaMs ) / 1000;
        const deltaDeg = shortestHeadingDeltaDegrees( this.lastRelativeAlphaDeg, relativeAlphaDeg );
        this.lastRelativeAlphaDeg = relativeAlphaDeg;
        this.lastRelativeAlphaMs = nowMs;

        if ( deltaSec < 0.01 || deltaSec > 0.4 || Math.abs( deltaDeg ) < 0.2 ) {
            return;
        }

        const instantRate = deltaDeg / deltaSec;
        const clampedRate = Math.max(
            -this.MAX_HEADING_RATE_DEG_PER_SEC,
            Math.min( this.MAX_HEADING_RATE_DEG_PER_SEC, instantRate ),
        );

        this.headingRateDegPerSec =
            this.HEADING_RATE_EMA_ALPHA * clampedRate +
            ( 1 - this.HEADING_RATE_EMA_ALPHA ) * this.headingRateDegPerSec;

        this.lastRawHeading = normalizeCompassAngle( this.lastRawHeading + deltaDeg );
        this.lastHeadingSampleMs = nowMs;
        this.emitCompassData( this.lastRawHeading );
    }

    /**
     * Handle device motion event (for sensor fusion)
     * @param event - Device motion event
     */
    private handleDeviceMotion = ( event: DeviceMotionEvent ): void => {
        if ( this.sensorFusion === null ) {
            return;
        }
        if ( event.accelerationIncludingGravity === null ) {
            return;
        }
        if ( event.rotationRate === null ) {
            return;
        }

        const sensorData: SensorData = {
            "accelerometer": [
                event.accelerationIncludingGravity.x ?? 0,
                event.accelerationIncludingGravity.y ?? 0,
                event.accelerationIncludingGravity.z ?? 0,
            ],
            "gyroscope": [
                event.rotationRate.alpha ?? 0,
                event.rotationRate.beta ?? 0,
                event.rotationRate.gamma ?? 0,
            ],
            "magnetometer": [ 0, 0, 0 ], // Magnetometer data not available from DeviceMotionEvent
            "timestamp":    Date.now(),
        };

        const fusionState = this.sensorFusion.update( sensorData );
        const heading = normalizeCompassAngle( fusionState.heading );

        this.acceptFilteredHeading( heading );
    };

    /**
     * Apply jump filter then emit an accepted heading.
     * The absolute sample is applied as-is: it is the north truth, and fluidity comes from
     * short prediction plus the display follow instead of smoothing the raw heading.
     * @param heading - Raw heading in degrees before calibration.
     */
    private acceptFilteredHeading( heading: number ): void {
        const filteredHeading = this.headingJumpFilter.filter( heading );
        const nowMs = Date.now();
        const previousHeading = this.lastRawHeading;
        this.updateHeadingRateFromSample( filteredHeading, nowMs );

        this.lastRawHeading = filteredHeading;
        this.lastHeadingSampleMs = nowMs;

        if (
            previousHeading === null ||
            Math.abs( shortestHeadingDeltaDegrees( previousHeading, filteredHeading ) ) >
            this.ABSOLUTE_MOTION_EPSILON_DEG
        ) {
            this.lastAbsoluteMotionMs = nowMs;
        }

        // Snap display toward the new absolute sample so turns stay reactive, not laggy.
        if ( this.displayHeadingDeg === null ) {
            this.displayHeadingDeg = this.lastRawHeading;
        } else {
            const catchUp = shortestHeadingDeltaDegrees( this.displayHeadingDeg, this.lastRawHeading );
            this.displayHeadingDeg = normalizeCompassAngle( this.displayHeadingDeg + catchUp * this.DISPLAY_ACCEPT_BLEND );
        }
        this.lastDisplayFollowMs = nowMs;

        this.emitCompassData( this.lastRawHeading );
    }

    /**
     * Update yaw-rate estimate from successive absolute (or accepted) heading samples.
     * @param heading - New raw heading in degrees.
     * @param nowMs - Sample timestamp in ms.
     */
    private updateHeadingRateFromSample( heading: number, nowMs: number ): void {
        if ( this.lastRawHeading === null || this.lastHeadingSampleMs <= 0 ) {
            this.headingRateDegPerSec = 0;

            return;
        }

        const deltaSec = ( nowMs - this.lastHeadingSampleMs ) / 1000;
        if ( deltaSec < 0.012 || deltaSec > 1.0 ) {
            return;
        }

        const deltaDeg = shortestHeadingDeltaDegrees( this.lastRawHeading, heading );

        // Tiny absolute chatter: pull rate toward zero instead of amplifying tremor.
        if ( Math.abs( deltaDeg ) < this.ABSOLUTE_MOTION_EPSILON_DEG ) {
            this.headingRateDegPerSec *= 0.5;
            if ( Math.abs( this.headingRateDegPerSec ) < 1 ) {
                this.headingRateDegPerSec = 0;
            }

            return;
        }

        // Ignore singularity-like jumps when estimating rate (they cause cone jerks).
        if ( Math.abs( deltaDeg ) > 60 ) {
            return;
        }

        const instantRate = deltaDeg / deltaSec;
        const clampedRate = Math.max(
            -this.MAX_HEADING_RATE_DEG_PER_SEC,
            Math.min( this.MAX_HEADING_RATE_DEG_PER_SEC, instantRate ),
        );

        this.headingRateDegPerSec =
            this.HEADING_RATE_EMA_ALPHA * clampedRate +
            ( 1 - this.HEADING_RATE_EMA_ALPHA ) * this.headingRateDegPerSec;
    }

    /**
     * Convert quaternion to heading
     * @param quaternion - Quaternion [x, y, z, w]
     * @returns Heading in degrees [0, 360)
     */
    private quaternionToHeading( quaternion: number[] ): number {
        const [ x, y, z, w ] = quaternion;

        // Calculate heading from quaternion
        const heading = Math.atan2(
            2 * ( w * z + x * y ),
            1 - 2 * ( y * y + z * z ),
        ) * 180 / Math.PI;

        return normalizeCompassAngle( heading );
    }

    /**
     * Emit compass data to listeners
     * @param rawHeading - Raw heading before calibration
     */
    private emitCompassData( rawHeading: number ): void {
        const calibratedHeading = normalizeCompassAngle( rawHeading + this.calibrationOffset );

        const compassData: CompassData = {
            "heading":    calibratedHeading,
            "calibrated": this.calibrated,
            "offset":     this.calibrationOffset,
            "timestamp":  Date.now(),
        };

        // Store last value
        this.lastValue = compassData;

        const event: NavigationEvent<CompassData> = {
            "type":      NavigationEventType.COMPASS_UPDATE,
            "data":      compassData,
            "timestamp": compassData.timestamp,
        };

        this.listeners.forEach( ( listener ) => {
            try {
                listener( event.data );
            } catch( error ) {
                console.error( "[RawCompassDataService] Error in listener:", error );
            }
        } );
    }

    /**
     * Subscribe to compass updates
     * @param listener - Callback function
     * @returns Unsubscribe function
     */
    public subscribe( listener: CompassEventListener ): () => void {
        this.listeners.add( listener );

        // Only push a seed value once a real sensor reading exists (avoid fake north at 0°).
        if ( this.hasValidReading() ) {
            listener( this.lastValue );
        }

        return () => {
            this.listeners.delete( listener );
        };
    }

    /**
     * Get number of active listeners
     * @returns Number of listeners
     */
    public getListenerCount(): number {
        return this.listeners.size;
    }

    /**
     * Inject compass data (for replay mode)
     * @param data - Compass data to inject
     */
    public injectData( data: CompassData ): void {
        // Store last value
        this.lastValue = data;

        // Update calibration state
        this.calibrated = data.calibrated;
        this.calibrationOffset = data.offset;

        // Emit to all listeners
        const event: NavigationEvent<CompassData> = {
            "type":      NavigationEventType.COMPASS_UPDATE,
            "data":      data,
            "timestamp": data.timestamp,
        };

        this.listeners.forEach( ( listener ) => {
            try {
                listener( event.data );
            } catch( error ) {
                console.error( "[RawCompassDataService] Error in listener:", error );
            }
        } );
    }

}

/**
 * Export singleton instance for convenience
 */
export const rawCompassDataService = RawCompassDataService.getInstance();

// Vite HMR: drop the singleton so the next module evaluation binds fresh listeners.
if ( import.meta.hot ) {
    import.meta.hot.dispose( () => {
        RawCompassDataService.resetInstanceForHotReload();
    } );
    import.meta.hot.accept();
}

